import { Button, Modal } from 'antd';
import { violationMessage } from './utils/AOPViolationUtils';
import { ExclamationCircleFilled } from '@ant-design/icons';
import React, { FC } from 'react';
import { AOPDraftPlan, AOPSuggestion, AOPViolation } from '../../types/AOPTypes';
import { stepTitle } from './utils/AOPPlanUtils';

/**
 * Why this draft cannot be published, block by block.
 *
 * Publish used to sit disabled with the reason in a tooltip, which meant the
 * one control that says "I am finished" answered by doing nothing. It is always
 * live now: it either publishes or opens this, and this names every block that
 * is holding it up and offers to take you there.
 *
 * The service decides whether a draft may be published. Nothing here re-checks
 * that — it reads the same payload back and sorts it by block.
 */

export interface AOPBlockedItem {
	/** The step this is about, or null when it is about the plan. */
	stepId: string | null;
	title: string;
	/** What is wrong, in the service's own words — one line each. */
	messages: string[];
}

/**
 * Everything still holding the draft up, grouped by the block it sits on.
 *
 * A violation and an open suggestion are different things — one is a rule the
 * plan breaks, the other a value the compiler filled in and wants confirmed —
 * but they stop a publish the same way, and to somebody looking at a list of
 * what is left they are one question: is this block finished. So they are
 * counted together, in the order the blocks run.
 */
export const blockedItemsOf = (
	plan: AOPDraftPlan | null,
	suggestions: AOPSuggestion[],
	violations: AOPViolation[]
): AOPBlockedItem[] => {
	const open = (suggestions ? suggestions : []).filter(
		(suggestion) => suggestion.status === 'open'
	);
	const all = violations ? violations : [];
	const items: AOPBlockedItem[] = [];

	(plan && plan.steps ? plan.steps : []).forEach((step) => {
		// Violations first: a rule the plan breaks has to be fixed, while a
		// suggestion only has to be ruled on.
		const messages = all
			.filter((violation) => violation.anchor === step.name)
			.map(violationMessage)
			.concat(
				open
					.filter((suggestion) => suggestion.anchor === step.name)
					.map((suggestion) => suggestion.message)
			);

		if (messages.length > 0) {
			items.push({ stepId: step.name, title: stepTitle(step), messages });
		}
	});

	// Anything about the plan rather than a block in it. There is nowhere to go,
	// so these carry no link — but leaving them out would mean a modal that
	// lists nothing above a Publish that still refuses.
	const planMessages = all
		.filter((violation) => !violation.anchor)
		.map(violationMessage)
		.concat(
			open.filter((suggestion) => !suggestion.anchor).map((suggestion) => suggestion.message)
		);

	if (planMessages.length > 0) {
		items.push({ stepId: null, title: 'This procedure', messages: planMessages });
	}

	return items;
};

const AOPPublishBlockedModal: FC<{
	items: AOPBlockedItem[];
	onGoToStep: (stepId: string) => void;
	onClose: () => void;
}> = ({ items, onGoToStep, onClose }) => {
	const blocks = items.length;

	return (
		<Modal
			visible={true}
			title="Missing fields"
			onCancel={onClose}
			footer={[
				<Button key="ok" type="primary" className="aop-sentence-case" onClick={onClose}>
					OK
				</Button>,
			]}
		>
			<div className="flex flex-col gap-m">
				<div className="flex items-start gap-xs">
					<span className="text-character-danger">
						<ExclamationCircleFilled />
					</span>
					<span className="flex flex-col gap-2xs">
						<span className="bodymedium text-character-primary">
							{blocks === 1
								? '1 block needs attention before publishing'
								: `${blocks} blocks need attention before publishing`}
						</span>
						<span className="bodyregular text-character-secondary">
							This procedure can&rsquo;t be published until every flagged block is complete.
						</span>
					</span>
				</div>

				<div className="flex flex-col rounded border border-solid border-grey-4">
					{items.map((item) => (
						<div
							key={item.stepId ? item.stepId : 'plan'}
							className="flex items-start gap-m border-0 border-b border-solid border-grey-4 px-s py-s last:border-b-0"
						>
							<span className="flex min-w-0 flex-1 flex-col gap-2xs">
								<span className="bodyregular text-character-primary">{item.title}</span>
								<span className="footnoteregular text-character-danger">
									{item.messages.length === 1
										? '1 missing field'
										: `${item.messages.length} missing fields`}
								</span>
								{/*
								  The service's own sentences. The count says how much is
								  left; these say what it is, which is the difference
								  between knowing a block is unfinished and knowing what to
								  do when you get there.
								*/}
								{item.messages.map((messageText, index) => (
									<span key={index} className="footnoteregular text-character-secondary">
										{messageText}
									</span>
								))}
							</span>
							{item.stepId ? (
								<Button
									type="link"
									className="aop-sentence-case shrink-0 px-0"
									onClick={() => onGoToStep(item.stepId as string)}
								>
									Go to block
								</Button>
							) : null}
						</div>
					))}
				</div>
			</div>
		</Modal>
	);
};

export default AOPPublishBlockedModal;
